import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Trophy, X } from "lucide-react";
import { api, C } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const pad = (n) => String(n).padStart(2, "0");
const countdown = (ms) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${pad(Math.floor(s / 60))}:${pad(s % 60)}`;
};

// "Your tournament starts in 2:00" — follows a registered player wherever they are (lobby or a cash table).
// Dismissing it only silences that phase: it comes back when the tournament actually starts, and keeps nudging
// every NUDGE_MS while their seat is still empty. Nobody is ever unregistered — an empty seat just blinds off.
const NUDGE_MS = 60000;

export const TournamentCall = () => {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [calls, setCalls] = useState([]);
  const [waitingFor, setWaitingFor] = useState("");   // they pressed TAKE SEAT before the cards were out
  const [, tick] = useState(0);
  const hushed = useRef({});  // event_id -> {phase, until}

  const signedIn = !!user?.user_id;
  const load = useCallback(() => {
    if (!signedIn) return;
    api.get(C.ApiRoutes.MTT_CALLS).then((r) => setCalls(r.data.calls)).catch(() => {});
  }, [signedIn]);
  useEffect(() => {
    load();
    const i = setInterval(load, waitingFor ? 2500 : 8000);   // waiting on the off: watch closely for the seat
    return () => clearInterval(i);
  }, [load, waitingFor]);

  // Seats are only dealt at the start. If they asked for theirs early, walk them in the moment it exists.
  useEffect(() => {
    if (!waitingFor) return;
    const mine = calls.find((c) => c.event_id === waitingFor && c.table_id);
    if (mine) {
      setWaitingFor("");
      nav(`/table/${mine.table_id}`);
    }
  }, [calls, waitingFor, nav]);
  useEffect(() => { const i = setInterval(() => tick((n) => n + 1), 1000); return () => clearInterval(i); }, []);

  const call = calls.find((c) => {
    if (pathname === `/table/${c.table_id}`) return false;            // already looking at it
    const h = hushed.current[c.event_id];
    return !h || h.phase !== c.status || Date.now() > h.until;
  });
  if (!signedIn || !call) return null;

  const started = call.status === "RUNNING";
  const dismiss = () => {
    hushed.current[call.event_id] = { phase: call.status, until: Date.now() + NUDGE_MS };
    tick((n) => n + 1);
  };
  const waiting = waitingFor === call.event_id;
  const takeSeat = () => {
    if (!call.table_id) {               // the off has not come yet: hold them, and walk them in when it does
      setWaitingFor(call.event_id);
      return;
    }
    if (pathname.startsWith("/table/")) window.dispatchEvent(new Event("pfp-leave-table"));  // stand up from the cash game
    setTimeout(() => nav(`/table/${call.table_id}`), pathname.startsWith("/table/") ? 350 : 0);
  };

  return (
    <div data-testid="tournament-call"
      className="fixed bottom-6 right-6 z-[70] w-[22rem] gold-card rounded-2xl p-5 flex flex-col gap-3 shadow-2xl rise">
      <button data-testid="tournament-call-dismiss" onClick={dismiss} aria-label="Dismiss"
        className="absolute top-3 right-3 text-slate-400 hover:text-slate-100 transition-colors"><X className="w-4 h-4" /></button>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-amber-300/80">
        <Trophy className="w-3.5 h-3.5" /> {started ? "Your tournament is under way" : "Your tournament is about to start"}
      </div>
      <div className="font-display text-amber-50 text-base leading-tight" data-testid="tournament-call-name">{call.name}</div>
      <div className="font-mono text-2xl text-amber-200" data-testid="tournament-call-clock">
        {started ? (call.table_no ? `Table ${call.table_no}` : "Seats dealt")
          : call.start_at - Date.now() > 0 ? countdown(call.start_at - Date.now()) : "Any moment"}
      </div>
      <p className="text-xs text-slate-400">
        {started
          ? "Your seat is waiting and the blinds are coming out of your stack until you sit down."
          : `Seats are dealt at the start — ${call.registered} registered. Take your seat and you'll be dealt in; stay away and you simply blind off.`}
      </p>
      <div className="flex items-center gap-2">
        <button data-testid="tournament-call-take-seat" onClick={takeSeat} disabled={waiting}
          className="btn-gold rounded-full px-5 py-2 text-xs font-display tracking-wider btn-lift flex-1 disabled:opacity-70">
          {waiting ? "SEATING YOU…" : "TAKE SEAT"}</button>
        <button data-testid="tournament-call-dismiss-btn" onClick={dismiss}
          className="rounded-full px-4 py-2 text-xs bg-white/5 hover:bg-white/15 transition-colors">Dismiss</button>
      </div>
      {waiting && (
        <p className="text-[10px] text-amber-200/80" data-testid="tournament-call-waiting">
          Holding you for the off — you will be walked to your table the second the cards come out.
        </p>
      )}
      {pathname.startsWith("/table/") && !started && (
        <p className="text-[10px] text-slate-500">Taking your seat leaves this table — your chips come back to your balance.</p>
      )}
    </div>
  );
};
